import {
  RowenaAvailableVariable,
  RowenaAvailableVariableGroup,
  RowenaVariableFormat,
} from 'types/api/SystemTenant/RowenaAI/RowenaContentTypes';

export const DEFAULT_FORMAT: RowenaVariableFormat = 'original';

const SPACE_RE = /[_\s]+/g;

export function applyFormat(value: string, format?: string | null): string {
  if (!value) return '';
  switch (format) {
    case 'lower':
      return value.toLowerCase();
    case 'upper':
      return value.toUpperCase();
    case 'title':
      return value
        .replace(SPACE_RE, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    case 'sentence':
      return value
        .replace(SPACE_RE, ' ')
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase());
    case 'number':
    case 'number-grouped':
    case 'compact':
    case 'original':
    default:
      return value;
  }
}

export const FORMAT_LABELS: Record<string, string> = {
  original: 'Original',
  lower: 'lower',
  upper: 'UPPER',
  title: 'Title Case',
  sentence: 'Sentence case',
  number: '1500',
  'number-grouped': '1,500',
  compact: '1.5K',
};

export const FORMAT_TOOLTIPS: Record<string, string> = {
  original: 'Use the value as resolved by the backend.',
  lower: 'Convert to lowercase.',
  upper: 'Convert to UPPERCASE.',
  title: 'Capitalize Every Word.',
  sentence: 'Capitalize first letter only.',
  number: 'Plain number (e.g. 1500).',
  'number-grouped': 'Grouped with thousands separator (e.g. 1,500).',
  compact: 'Compact notation (e.g. 1.5K).',
};

export function pickDefaultFormat(variable: RowenaAvailableVariable): RowenaVariableFormat {
  if (variable.supportedFormats?.includes(DEFAULT_FORMAT)) return DEFAULT_FORMAT;
  return variable.supportedFormats?.[0] ?? DEFAULT_FORMAT;
}

export function pickDefaultOption(variable: RowenaAvailableVariable): string | null {
  return variable.options?.[0]?.value ?? null;
}

export function flattenAvailableVariables(
  groups: RowenaAvailableVariableGroup[] | undefined
): Record<string, RowenaAvailableVariable> {
  const map: Record<string, RowenaAvailableVariable> = {};
  const walk = (gs: RowenaAvailableVariableGroup[] | undefined) => {
    (gs ?? []).forEach((group) => {
      group.variables.forEach((variable) => {
        map[variable.name] = variable;
      });
      walk(group.groups);
    });
  };
  walk(groups);
  return map;
}

interface DocLikeNode {
  type: { name: string };
  attrs: Record<string, unknown>;
}

interface DocLike {
  descendants: (cb: (node: DocLikeNode, pos: number) => boolean | void) => void;
}

function formatOrphanList(orphans: OrphanReference[]): string {
  return orphans.map((o) => `@${o.name}`).join(', ');
}

export { formatOrphanList };

export interface OrphanReference {
  name: string;
  nodeType: 'mention' | 'rowenaConditional';
  pos: number;
  attrs: Record<string, unknown>;
}

export function findOrphanReferences(
  doc: DocLike,
  variablesByName: Record<string, RowenaAvailableVariable>
): OrphanReference[] {
  const orphans: OrphanReference[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name === 'mention') {
      const id = node.attrs.id as string | null | undefined;
      const label = node.attrs.label as string | null | undefined;
      const idKnown = !!id && !!variablesByName[id];
      const labelKnown = !!label && !!variablesByName[label];
      // A mention is orphan only when NEITHER id nor label maps to a known
      // variable. This tolerates legacy data where id and label diverged.
      if (!idKnown && !labelKnown && (id || label)) {
        orphans.push({ name: (id || label) ?? '', nodeType: 'mention', pos, attrs: node.attrs });
      }
    } else if (node.type.name === 'rowenaConditional') {
      const name = node.attrs.variable as string | null | undefined;
      if (name && !variablesByName[name]) {
        orphans.push({ name, nodeType: 'rowenaConditional', pos, attrs: node.attrs });
      }
    }
    return true;
  });
  return orphans;
}
